import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/Button'
import {
  toContactPayload,
  CONTACT_TOPICS,
  type ContactInput,
} from '@/lib/validators'
import { site } from '@/config/site'
import { cn } from '@/lib/cn'

type Status = 'idle' | 'submitting' | 'success' | 'error'

export function Contact() {
  const headingId = useId()
  const [status, setStatus] = useState<Status>('idle')
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactInput>({ mode: 'onTouched' })

  const topics = watch('topics') || []
  const otherSelected = Array.isArray(topics) && topics.includes('Other')

  async function onSubmit(values: ContactInput) {
    setStatus('submitting')
    setServerError(null)
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toContactPayload(values)),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        setStatus('error')
        setServerError(
          body?.error === 'validation_failed'
            ? 'Please fix the highlighted fields and try again.'
            : res.status === 429
              ? 'Too many messages in a short time. Please try again in a few minutes.'
              : 'We could not send your message. Please try again in a moment.',
        )
        return
      }
      reset()
      setStatus('success')
    } catch {
      setStatus('error')
      setServerError(
        'Network issue. Please check your connection and try again.',
      )
    }
  }

  const inputBase =
    'block w-full bg-surface border-2 px-4 py-3 text-base text-ink-deep ' +
    'placeholder:text-ink-faint focus-visible:outline-none focus-visible:border-brand ' +
    'transition-colors duration-200 ease-out-quart'

  function inputCls(hasError: boolean) {
    return cn(inputBase, hasError ? 'border-red-600' : 'border-ink-deep/15')
  }

  return (
    <section
      id="contact"
      aria-labelledby={headingId}
      className="bg-surface-alt py-section-y"
    >
      <div className="mx-auto grid max-w-[88rem] grid-cols-1 gap-x-12 gap-y-14 px-6 sm:px-10 lg:grid-cols-12">
        <header className="lg:col-span-5">
          <span className="eyebrow">Contact</span>
          <h2
            id={headingId}
            className="mt-4 font-display text-display-2 font-extrabold text-ink-deep"
          >
            Let&rsquo;s Connect!
          </h2>
          <p className="measure mt-5 text-base leading-relaxed text-ink-slate sm:text-lg">
            Learn more about GIA Longevity, partnership opportunities, speak
            to GIA Longevity Leadership, share a resource, or sign up for
            updates.
          </p>

          <dl className="mt-10 space-y-4 border-t-2 border-ink-deep pt-6 text-sm">
            <ContactRow term="Email" value={site.contactEmail} href={`mailto:${site.contactEmail}`} />
            <ContactRow term="Phone" value={site.contactPhone} />
            <ContactRow term="Global" value="gialongevity.org" href={site.globalUrl} />
          </dl>
        </header>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="lg:col-span-6 lg:col-start-7"
          aria-describedby={`${headingId}-status`}
        >
          <div className="space-y-5">
            <Field
              id="contact-name"
              label="Name"
              required
              error={errors.name?.message}
              autoComplete="name"
              registration={register('name', { required: 'Please enter your name' })}
              cls={inputCls(!!errors.name)}
            />
            <Field
              id="contact-email"
              label="Email"
              type="email"
              required
              error={errors.email?.message}
              autoComplete="email"
              registration={register('email', {
                required: 'Please enter a valid email',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email' },
              })}
              cls={inputCls(!!errors.email)}
            />
            <Field
              id="contact-org"
              label="Organization"
              hint="Optional"
              autoComplete="organization"
              registration={register('organization')}
              cls={inputCls(!!errors.organization)}
            />

            {/* Contact me about — multi-select per Website Review */}
            <fieldset>
              <legend className="text-xs font-bold uppercase tracking-[0.12em] text-ink-deep">
                Contact me about
                <span aria-hidden className="ml-1 text-red-600">*</span>
                <span className="ml-2 normal-case tracking-normal text-ink-muted">
                  Pick one or more
                </span>
              </legend>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {CONTACT_TOPICS.map((topic) => (
                  <label
                    key={topic}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 border-2 bg-surface px-4 py-3 text-sm font-medium text-ink-deep',
                      'transition-colors duration-200 ease-out-quart',
                      'has-[:checked]:border-brand has-[:checked]:bg-brand-soft/40',
                      errors.topics ? 'border-red-600' : 'border-ink-deep/15',
                    )}
                  >
                    <input
                      type="checkbox"
                      value={topic}
                      {...register('topics', {
                        validate: (v) =>
                          (Array.isArray(v) && v.length > 0) ||
                          'Please select at least one option',
                      })}
                      className="h-4 w-4 accent-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    />
                    {topic}
                  </label>
                ))}
              </div>
              {errors.topics && (
                <p className="mt-2 text-xs font-bold text-red-600" role="alert">
                  {errors.topics.message as string}
                </p>
              )}
            </fieldset>

            {/* Free-text message — shown when "Other" is selected, no character limit */}
            {otherSelected && (
              <div>
                <label
                  htmlFor="contact-message"
                  className="flex items-baseline justify-between text-xs font-bold uppercase tracking-[0.12em] text-ink-deep"
                >
                  <span>
                    Your message
                    <span aria-hidden className="ml-1 text-red-600">*</span>
                  </span>
                </label>
                <textarea
                  id="contact-message"
                  rows={5}
                  {...register('message', {
                    validate: (v) =>
                      !otherSelected ||
                      (typeof v === 'string' && v.trim().length > 0) ||
                      'Please type a message',
                  })}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'contact-message-error' : undefined}
                  className={cn('mt-2 resize-y', inputCls(!!errors.message))}
                  placeholder="What would you like us to know?"
                />
                {errors.message && (
                  <p id="contact-message-error" className="mt-2 text-xs font-bold text-red-600" role="alert">
                    {errors.message.message}
                  </p>
                )}
              </div>
            )}

            {/* Honeypot */}
            <div className="hidden" aria-hidden>
              <label htmlFor="contact-website">Website</label>
              <input id="contact-website" type="text" tabIndex={-1} autoComplete="off" {...register('website')} />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Button type="submit" disabled={isSubmitting} size="lg">
              {isSubmitting ? 'Sending…' : 'Send message'}
            </Button>
            <p id={`${headingId}-status`} aria-live="polite" className="text-sm">
              {status === 'success' && (
                <span className="font-bold text-ink-deep">
                  Thanks. We&rsquo;ll be in touch shortly.
                </span>
              )}
              {status === 'error' && serverError && (
                <span className="font-bold text-red-600">{serverError}</span>
              )}
            </p>
          </div>

          {/* Opt-in language — TODO(client): final legal copy pending */}
          <p className="mt-5 max-w-prose text-xs leading-relaxed text-ink-muted">
            By submitting this form you agree to be contacted by{' '}
            {site.name} about your request and, if you selected updates, to
            receive occasional news from us. You can unsubscribe at any
            time. See our{' '}
            <a
              href="/privacy"
              className="font-bold underline decoration-1 underline-offset-2 hover:no-underline"
            >
              privacy statement
            </a>
            .
          </p>
        </form>
      </div>
    </section>
  )
}

function ContactRow({ term, value, href }: { term: string; value: string; href?: string }) {
  const Body = href ? (
    <a
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="font-bold text-ink-deep underline decoration-2 underline-offset-4 hover:text-brand-strong hover:no-underline"
    >
      {value}
    </a>
  ) : (
    <span className="font-bold text-ink-deep">{value}</span>
  )
  return (
    <div className="flex items-baseline justify-between gap-6">
      <dt className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-ink-muted">
        {term}
      </dt>
      <dd className="text-sm tabular">{Body}</dd>
    </div>
  )
}

interface FieldProps {
  id: string
  label: string
  type?: string
  required?: boolean
  hint?: string
  error?: string
  autoComplete?: string
  registration: ReturnType<ReturnType<typeof useForm<ContactInput>>['register']>
  cls: string
}

function Field({ id, label, type = 'text', required, hint, error, autoComplete, registration, cls }: FieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="flex items-baseline justify-between text-xs font-bold uppercase tracking-[0.12em] text-ink-deep"
      >
        <span>
          {label}
          {required && <span aria-hidden className="ml-1 text-red-600">*</span>}
        </span>
        {hint && <span className="text-ink-muted normal-case tracking-normal">{hint}</span>}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        {...registration}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn('mt-2', cls)}
      />
      {error && (
        <p id={`${id}-error`} className="mt-2 text-xs font-bold text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
